import { useTranslation } from "react-i18next";
import { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import { CustomerDisplayFieldSelect } from "./CustomerDisplayFieldSelect";
import { FormSection } from "../../../components/FormSection";
import { getEffectiveBookkeepingMapping } from "@KassaroAB/shared/utils/bookkeepingDefaultAccounts";
import type { PaymentMethodOrder } from "@KassaroAB/shared/types/paymentMethods/paymentMethod";
import { FieldGroupPaymentMethodFields } from "../paymentMethodFieldGroup";
import { usePaymentMethodForm } from "../paymentMethodFormHook";
import {
  PaymentMethodFormContainer,
  type PaymentMethodFormProps,
} from "./PaymentMethodFormContainer";
import { basePaymentMethodValues } from "./basePaymentMethodValues";
import { Fields, IntegrationField, TextField, ToggleField } from "./paymentMethodFields";

export const OrderForm = ({
  formId,
  store,
  paymentMethod,
  savedTick,
  onSubmit,
  onDirtyChange,
}: PaymentMethodFormProps<Omit<PaymentMethodOrder, "id">>) => {
  const { t } = useTranslation();
  const mergedAccounts = getEffectiveBookkeepingMapping(
    store.country,
    store.bookkeepingDefaultAccounts,
  );
  const form = usePaymentMethodForm({
    defaultValues: {
      ...basePaymentMethodValues(paymentMethod),
      integrationId: paymentMethod.integrationId ?? "",
      documentHeaderText: paymentMethod.documentHeaderText ?? "",
      customerSecondaryField: paymentMethod.customerSecondaryField,
      customerThirdField: paymentMethod.customerThirdField,
      includeOrderDate: paymentMethod.includeOrderDate ?? true,
      printSignatureReceipt: paymentMethod.printSignatureReceipt ?? false,
      emailDocumentToStore: paymentMethod.emailDocumentToStore ?? false,
    },
    onSubmit: ({ value }) =>
      onSubmit({ ...value, integrationId: value.integrationId || null }),
  });

  return (
    <PaymentMethodFormContainer
      formId={formId}
      form={form}
      savedTick={savedTick}
      onDirtyChange={onDirtyChange}
    >
      <FieldGroupPaymentMethodFields
        form={form}
        fields={{
          name: "name",
          type: "type",
          bookkeepingAccountId: "bookkeepingAccountId",
          color: "color",
        }}
        defaultAccountNumber={mergedAccounts.businessAccount}
        t={t}
      />
      <FormSection title={t("paymentMethodForm.additionalSettings")}>
        <Fields>
          <form.Field name="integrationId">
            {(field) => (
              <IntegrationField
                label={t("paymentMethodForm.integration")}
                placeholder={t("paymentMethodForm.noIntegration")}
                chainId={store.chainId}
                integrationTypes={[IntegrationType.FORTNOX, IntegrationType.SPIRIS, IntegrationType.BJORN_LUNDEN]}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                errors={field.state.meta.errors}
              />
            )}
          </form.Field>
          <form.Field name="documentHeaderText">
            {(field) => (
              <TextField
                label={t("paymentMethodForm.order.documentHeaderText")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                placeholder={t("paymentMethodForm.documentHeaderTextPlaceholder")}
              />
            )}
          </form.Field>
          <form.Field name="customerSecondaryField">
            {(field) => (
              <CustomerDisplayFieldSelect
                label={t("paymentMethodForm.customerSecondaryField")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="customerThirdField">
            {(field) => (
              <CustomerDisplayFieldSelect
                label={t("paymentMethodForm.customerThirdField")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="includeOrderDate">
            {(field) => (
              <ToggleField
                label={t("paymentMethodForm.order.includeOrderDate")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="emailDocumentToStore">
            {(field) => (
              <ToggleField
                label={t("paymentMethodForm.emailDocumentToStore")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="printSignatureReceipt">
            {(field) => (
              <ToggleField
                label={t("paymentMethodForm.printSignatureReceipt")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
        </Fields>
      </FormSection>
    </PaymentMethodFormContainer>
  );
};
